/**
 * Textarea Component
 *
 * A multi-line text input component.
 *
 * @example
 * ```tsx
 * <Textarea placeholder="Enter your message..." />
 * ```
 */

'use client';

import * as React from 'react';
import { cn } from '../utils';

// =============================================================================
// TEXTAREA
// =============================================================================

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Error state styling */
  error?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, error, ...props }, ref) => {
    return (
      <textarea
        ref={ref}
        data-slot="textarea"
        className={cn(
          // Base styling - match Input component
          'flex min-h-20 w-full rounded-(--input-radius) bg-background px-3 py-2 text-sm',
          // Border styling - match Input component (2px border with design token)
          'border-2 border-(--input-border)',
          'ring-offset-background placeholder:text-(--input-placeholder)',
          // Focus styling - match Input component
          'focus-visible:outline-none focus-visible:border-(--input-border-focus) focus-visible:ring-0',
          'disabled:cursor-not-allowed disabled:opacity-50',
          error && 'border-destructive focus-visible:border-destructive',
          className,
        )}
        {...props}
      />
    );
  },
);
Textarea.displayName = 'Textarea';

// =============================================================================
// EXPORTS
// =============================================================================

export { Textarea };
